.Button {
  --primary-color: var(--primary);
  --primary-color-hover: var(--primary-1);
  --text-color: var(--neutral-4);
  --loading-icon-color: var(--neutral-2);

  position: relative;
  cursor: pointer;
  user-select: none;
  border-radius: var(--radius-xl);
  border: 1px solid transparent;
  padding: var(--space-sm) var(--space);
  font-size: 0.9375rem;
  font-weight: normal;
  color: var(--text-color);
  background: var(--primary-color);
  min-height: fit-content;
  transition:
    color 250ms,
    background 250ms,
    border-color 250ms;
  text-wrap: nowrap;
  text-decoration: none;

  &:not([disabled]):hover {
    background: var(--primary-color-hover);
  }

  &:focus-visible,
  &:global(.focus-visible) {
    outline-offset: 2px;
  }

  &[disabled] {
    --primary-color: var(--neutral-3);
    --text-color: var(--faded);

    cursor: not-allowed;
  }

  &.loading {
    --primary-color: var(--primary-color-hover);
    --text-color: transparent;

    pointer-events: none;
    position: relative;

    &:after {
      position: absolute;
      top: calc(50% - 0.5rem);
      left: calc(50% - 0.5rem);
      display: block;
      width: 1rem;
      aspect-ratio: 1;
      border-radius: var(--radius-lg);
      border: 2px solid var(--loading-icon-color);
      border-right-color: transparent;
      content: "";
      animation: 850ms linear infinite loading;
    }
  }

  &[disabled].loading {
    background: var(--neutral-3);

    &:after {
      border-color: var(--faded);
      border-right-color: transparent;
    }
  }

  [data-slot="icon"] {
    min-width: 16px;
    width: 16px;
    height: 16px;
  }

  &:has(> [data-slot="icon"]) {
    display: inline-flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: var(--space-sm);
  }

  /* Button has an icon as the first child */
  &:has(> [data-slot="icon"]:first-child) {
    padding-left: var(--space-lg);
  }

  /* Button has an icon as the last child */
  &:has(> [data-slot="icon"]:last-child) {
    padding-right: var(--space-lg);
  }
}

@keyframes loading {
  to {
    transform: rotate(360deg);
  }
}

/* COLORS */
.color-accent-1 {
  --primary-color: var(--accent-1);
  --primary-color-hover: var(--accent-1-80);
  --text-color: var(--black);
  --loading-icon-color: var(--neutral);
}

.color-accent-2 {
  --primary-color: var(--accent-2);
  --primary-color-hover: var(--accent-2-80);
}

.color-neutral {
  --primary-color: var(--neutral);
  --primary-color-hover: var(--neutral-hover);
  --text-color: var(--white);
  --loading-icon-color: var(--neutral-2);
}

.color-secondary {
  --primary-color: var(--secondary);
  --primary-color-hover: var(--secondary-80);
}

/* VARIANTS */
.variant-outline {
  border-color: var(--neutral-2);
  color: var(--primary-color);
  background: none;

  &:not([disabled]):hover {
    border-color: var(--primary-color);
    color: var(--primary-color-hover);
    background: none;
  }

  &.loading,
  &.loading:hover {
    background: none;
    color: transparent;

    &:after {
      border: 2px solid var(--primary-color);
      border-right-color: transparent;
    }
  }
}

.variant-underlined {
  --text-color: var(--primary-color);

  text-decoration: underline;
  background-color: transparent;

  &:not([disabled]):hover {
    background: inherit;
  }

  &.size-sm {
    padding: var(--space-xs) var(--space-sm);
  }
}

.variant-ghost {
  --text-color: var(--neutral);
  border-radius: 2px;
  padding: 0;
  font-size: 0.875rem;
  background: none;

  &:hover,
  &:not(:disabled):hover {
    --text-color: var(--primary-color);
    background: transparent;
  }

  &:has(> [data-slot="icon"]) {
    gap: var(--space-xs);
  }

  [data-slot="icon"] {
    min-width: 12px;
    width: 12px;
    height: 12px;
  }

  &:has(> [data-slot="icon"]:first-child) {
    padding-left: 0;
  }

  /* Button has an icon as the last child */
  &:has(> [data-slot="icon"]:last-child) {
    padding-right: 0;
  }
}

.size-sm {
  padding: var(--space-xs) var(--space-md);
  font-size: 0.875rem;

  &:has(> [data-slot="icon"]) {
    gap: var(--space-xs);
  }

  /* Button has an icon as the first child */
  &:has(> [data-slot="icon"]:first-child) {
    padding-left: var(--space-sm);
  }

  /* Button has an icon as the last child */
  &:has(> [data-slot="icon"]:last-child) {
    padding-right: var(--space-sm);
  }

  [data-slot="icon"] {
    min-width: 12px;
    width: 12px;
    height: 12px;
  }
}

.icon-true,
.Button:has([data-slot="icon"]:only-child) {
  justify-content: center;
  align-items: center;
  aspect-ratio: 1;
  width: 39px;
  padding: 0;

  &:has(> [data-slot="icon"]) {
    display: flex;
  }

  &:has(> [data-slot="icon"]:first-child) {
    padding: 0;
  }

  span {
    display: none;
    visibility: hidden;
  }

  &[data-size="sm"] {
    width: 28px;
  }
}
